import React, { useState, useEffect } from 'react';
import { 
    Building2, Plus, Search, CheckCircle, XCircle, AlertCircle, Clock, 
    Users as UsersIcon, Edit3, CreditCard, Key, Trash2, Shield, 
    UserPlus, DollarSign, ChevronDown, ChevronUp, RefreshCw,
    Ban, Play, Activity
} from 'lucide-react';
import api from '../services/api';
import Modal from '../components/Common/Modal';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import Pagination from '../components/Common/Pagination';

const PAGE_SIZE = 20;

const SuperAdminTenants = () => {
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalTenants, setTotalTenants] = useState(0);
    
    // Users modal pagination
    const [usersPage, setUsersPage] = useState(1);
    const [usersTotalPages, setUsersTotalPages] = useState(1);
    const [usersTotal, setUsersTotal] = useState(0);
    const USERS_PAGE_SIZE = 20;

    // Create Tenant Modal
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [formData, setFormData] = useState({
        company_name: '',
        subdomain: '',
        landlord_email: '',
        landlord_password: '',
        landlord_first_name: '',
        landlord_last_name: '',
        landlord_phone: ''
    });
    const [creating, setCreating] = useState(false);
    const [createResult, setCreateResult] = useState(null);

    // Edit Tenant Modal
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingTenant, setEditingTenant] = useState(null);
    const [editFormData, setEditFormData] = useState({
        company_name: '',
        subdomain: '',
        trial_ends_at: ''
    });
    const [editing, setEditing] = useState(false);
    const [editResult, setEditResult] = useState(null);

    // Users Modal
    const [showUsersModal, setShowUsersModal] = useState(false);
    const [usersTenant, setUsersTenant] = useState(null);
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [showAddUserModal, setShowAddUserModal] = useState(false);
    const [addUserForm, setAddUserForm] = useState({
        email: '',
        phone: '',
        password: '',
        role: 'tenant',
        first_name: '',
        last_name: ''
    });
    const [addingUser, setAddingUser] = useState(false);
    const [addUserResult, setAddUserResult] = useState(null);
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
    const [passwordUserId, setPasswordUserId] = useState(null);
    const [passwordUserEmail, setPasswordUserEmail] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [changingPassword, setChangingPassword] = useState(false);
    const [passwordResult, setPasswordResult] = useState(null);
    const [showDeleteUserConfirm, setShowDeleteUserConfirm] = useState(false);
    const [deleteUserId, setDeleteUserId] = useState(null);
    const [deleteUserEmail, setDeleteUserEmail] = useState('');

    // Edit User Modal
    const [showEditUserModal, setShowEditUserModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [editUserForm, setEditUserForm] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        role: 'tenant'
    });
    const [editingUserSaving, setEditingUserSaving] = useState(false);
    const [editUserResult, setEditUserResult] = useState(null);

    // Credits Modal
    const [showCreditsModal, setShowCreditsModal] = useState(false);
    const [creditsTenant, setCreditsTenant] = useState(null);
    const [creditsData, setCreditsData] = useState({ balance: 0, transactions: [] });
    const [creditsLoading, setCreditsLoading] = useState(false);
    const [showAddCreditsModal, setShowAddCreditsModal] = useState(false);
    const [addCreditsForm, setAddCreditsForm] = useState({ credits: '', amount: '' });
    const [addingCredits, setAddingCredits] = useState(false);
    const [addCreditsResult, setAddCreditsResult] = useState(null);

    // Suspend/Activate
    const [suspendingId, setSuspendingId] = useState(null);
    const [showSuspendModal, setShowSuspendModal] = useState(false);
    const [suspendTenantId, setSuspendTenantId] = useState(null);
    const [suspendReason, setSuspendReason] = useState('');
    const [suspending, setSuspending] = useState(false);

    useEffect(() => {
        fetchTenants();
    }, []);

    // Re-fetch when tenant page changes
    useEffect(() => {
        if (!loading) fetchTenants();
    }, [page]);

    const fetchTenants = async () => {
        try {
            setLoading(true);
            const response = await api.get('/tenants', {
                params: { page, limit: PAGE_SIZE }
            });
            let data = [];
            if (response.data?.success) {
                data = response.data.data?.data || response.data.data || [];
                const pagination = response.data.data?.pagination;
                if (pagination) {
                    setTotalPages(pagination.pages || 1);
                    setTotalTenants(pagination.total || 0);
                }
            }
            setTenants(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error fetching tenants:', error);
            setTenants([]);
        } finally {
            setLoading(false);
        }
    };


    // ==================== CREATE TENANT ====================
    const handleCreateTenant = async (e) => {
        e.preventDefault();
        setCreating(true);
        setCreateResult(null);
        try {
            const response = await api.post('/admin/tenants', formData);
            setCreateResult({ success: true, data: response.data.data, message: response.data.message });
            setFormData({
                company_name: '',
                subdomain: '',
                landlord_email: '',
                landlord_password: '',
                landlord_first_name: '',
                landlord_last_name: '',
                landlord_phone: ''
            });
            fetchTenants();
        } catch (error) {
            setCreateResult({ 
                success: false, 
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to create tenant' 
            });
        } finally {
            setCreating(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'company_name') {
            const subdomain = value.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 50);
            setFormData(prev => ({ ...prev, company_name: value, subdomain }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    // ==================== EDIT TENANT ====================
    const openEditModal = async (tenant) => {
        setEditingTenant(tenant);
        setEditFormData({
            company_name: tenant.company_name || '',
            subdomain: tenant.subdomain || '',
            trial_ends_at: tenant.trial_ends_at ? tenant.trial_ends_at.split('T')[0] : ''
        });
        setEditResult(null);
        setShowEditModal(true);
    };

    const handleEditTenant = async (e) => {
        e.preventDefault();
        setEditing(true);
        setEditResult(null);
        try {
            const response = await api.put(`/admin/tenants/${editingTenant.id}`, editFormData);
            setEditResult({ success: true, message: response.data.message || 'Tenant updated successfully' });
            fetchTenants();
        } catch (error) {
            setEditResult({ 
                success: false, 
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to update tenant' 
            });
        } finally {
            setEditing(false);
        }
    };

    // ==================== SUSPEND / ACTIVATE ====================
    const handleSuspendTenant = async () => {
        setSuspending(true);
        try {
            await api.put(`/admin/tenants/${suspendTenantId}/suspend`);
            setShowSuspendModal(false);
            setSuspendTenantId(null);
            fetchTenants();
        } catch (error) {
            alert(error.response?.data?.error || 'Failed to suspend tenant');
        } finally {
            setSuspending(false);
        }
    };

    const handleActivateTenant = async (tenantId) => {
        if (!window.confirm('Are you sure you want to activate this tenant?')) return;
        try {
            await api.put(`/admin/tenants/${tenantId}/activate`);
            fetchTenants();
        } catch (error) {
            alert(error.response?.data?.error || 'Failed to activate tenant');
        }
    };

    // ==================== USERS MANAGEMENT ====================
    const openUsersModal = async (tenant) => {
        setUsersTenant(tenant);
        setShowUsersModal(true);
        setUsersLoading(true);
        setUsers([]);
        try {
            const response = await api.get(`/admin/tenants/${tenant.id}/users`, {
                params: { page: 1, limit: 50 }
            });
            if (response.data?.success) {
                setUsers(response.data.data?.data || []);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            setUsers([]);
        } finally {
            setUsersLoading(false);
        }
    };

    const openAddUserModal = () => {
        setAddUserForm({
            email: '',
            phone: '',
            password: '',
            role: 'tenant',
            first_name: '',
            last_name: ''
        });
        setAddUserResult(null);
        setShowAddUserModal(true);
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        setAddingUser(true);
        setAddUserResult(null);
        try {
            const response = await api.post(`/admin/tenants/${usersTenant.id}/users`, addUserForm);
            setAddUserResult({ success: true, message: response.data.message || 'User added successfully' });
            setAddUserForm({
                email: '',
                phone: '',
                password: '',
                role: 'tenant',
                first_name: '',
                last_name: ''
            });
            // Refresh users list
            const usersResponse = await api.get(`/admin/tenants/${usersTenant.id}/users`, {
                params: { page: 1, limit: 50 }
            });
            if (usersResponse.data?.success) {
                setUsers(usersResponse.data.data?.data || []);
            }
        } catch (error) {
            setAddUserResult({ 
                success: false, 
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to add user' 
            });
        } finally {
            setAddingUser(false);
        }
    };

    const openChangePasswordModal = (userId, email) => {
        setPasswordUserId(userId);
        setPasswordUserEmail(email);
        setNewPassword('');
        setPasswordResult(null);
        setShowChangePasswordModal(true);
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setChangingPassword(true);
        setPasswordResult(null);
        try {
            await api.put(`/admin/tenants/${usersTenant.id}/users/${passwordUserId}/password`, {
                new_password: newPassword
            });
            setPasswordResult({ success: true, message: 'Password changed successfully' });
            setNewPassword('');
        } catch (error) {
            setPasswordResult({ 
                success: false, 
                error: error.response?.data?.error || 'Failed to change password' 
            });
        } finally {
            setChangingPassword(false);
        }
    };

    const confirmDeleteUser = (userId, email) => {
        setDeleteUserId(userId);
        setDeleteUserEmail(email);
        setShowDeleteUserConfirm(true);
    };

    const handleDeleteUser = async () => {
        try {
            await api.delete(`/admin/tenants/${usersTenant.id}/users/${deleteUserId}`);
            setShowDeleteUserConfirm(false);
            setDeleteUserId(null);
            setDeleteUserEmail('');
            // Refresh users
            const response = await api.get(`/admin/tenants/${usersTenant.id}/users`, {
                params: { page: 1, limit: 50 }
            });
            if (response.data?.success) {
                setUsers(response.data.data?.data || []);
            }
        } catch (error) {
            alert(error.response?.data?.error || 'Failed to delete user');
        }
    };

    // ==================== EDIT USER ====================
    const openEditUserModal = (user) => {
        setEditingUser(user);
        setEditUserForm({
            first_name: user.first_name || '',
            last_name: user.last_name || '',
            email: user.email || '',
            phone: user.phone || '',
            role: user.role || 'tenant'
        });
        setEditUserResult(null);
        setShowEditUserModal(true);
    };

    const handleEditUser = async (e) => {
        e.preventDefault();
        setEditingUserSaving(true);
        setEditUserResult(null);
        try {
            const response = await api.put(`/admin/tenants/${usersTenant.id}/users/${editingUser.id}`, editUserForm);
            setEditUserResult({ success: true, message: response.data.message || 'User updated successfully' });
            // Refresh users list
            const usersResponse = await api.get(`/admin/tenants/${usersTenant.id}/users`, {
                params: { page: 1, limit: 50 }
            });
            if (usersResponse.data?.success) {
                setUsers(usersResponse.data.data?.data || []);
            }
        } catch (error) {
            setEditUserResult({ 
                success: false, 
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to update user' 
            });
        } finally {
            setEditingUserSaving(false);
        }
    };

    // ==================== CREDITS MANAGEMENT ====================
    const openCreditsModal = async (tenant) => {
        setCreditsTenant(tenant);
        setShowCreditsModal(true);
        setCreditsLoading(true);
        setCreditsData({ balance: 0, transactions: [] });
        try {
            const [creditsRes, transactionsRes] = await Promise.all([
                api.get(`/admin/tenants/${tenant.id}/credits`),
                api.get(`/admin/tenants/${tenant.id}/credits/transactions`, {
                    params: { page: 1, limit: 50 }
                })
            ]);
            setCreditsData({
                balance: creditsRes.data?.data?.balance || 0,
                transactions: transactionsRes.data?.data?.data || []
            });
        } catch (error) {
            console.error('Error fetching credits:', error);
        } finally {
            setCreditsLoading(false);
        }
    };

    const openAddCreditsModal = () => {
        setAddCreditsForm({ credits: '', amount: '' });
        setAddCreditsResult(null);
        setShowAddCreditsModal(true);
    };

    const handleAddCredits = async (e) => {
        e.preventDefault();
        setAddingCredits(true);
        setAddCreditsResult(null);
        try {
            const response = await api.post(`/admin/tenants/${creditsTenant.id}/credits`, {
                credits: parseInt(addCreditsForm.credits),
                amount: addCreditsForm.amount ? parseFloat(addCreditsForm.amount) : undefined
            });
            setAddCreditsResult({ success: true, message: response.data.message || 'Credits added successfully' });
            setAddCreditsForm({ credits: '', amount: '' });
            // Refresh credits data
            const [creditsRes, transactionsRes] = await Promise.all([
                api.get(`/admin/tenants/${creditsTenant.id}/credits`),
                api.get(`/admin/tenants/${creditsTenant.id}/credits/transactions`, {
                    params: { page: 1, limit: 50 }
                })
            ]);
            setCreditsData({
                balance: creditsRes.data?.data?.balance || 0,
                transactions: transactionsRes.data?.data?.data || []
            });
        } catch (error) {
            setAddCreditsResult({ 
                success: false, 
                error: error.response?.data?.error || 'Failed to add credits' 
            });
        } finally {
            setAddingCredits(false);
        }
    };

    // ==================== UI HELPERS ====================
    const getStatusBadge = (status) => {
        const colors = {
            active: 'bg-green-100 text-green-800',
            suspended: 'bg-red-100 text-red-800',
            trial: 'bg-yellow-100 text-yellow-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'active': return <CheckCircle className="h-4 w-4 text-green-500" />;
            case 'suspended': return <XCircle className="h-4 w-4 text-red-500" />;
            case 'trial': return <Clock className="h-4 w-4 text-yellow-500" />;
            default: return <AlertCircle className="h-4 w-4 text-gray-500" />;
        }
    };

    const getUserRoleBadge = (role) => {
        const colors = {
            superadmin: 'bg-purple-100 text-purple-800',
            landlord: 'bg-blue-100 text-blue-800',
            caretaker: 'bg-orange-100 text-orange-800',
            tenant: 'bg-green-100 text-green-800'
        };
        return colors[role] || 'bg-gray-100 text-gray-800';
    };

    const getUserStatusBadge = (status) => {
        const colors = {
            active: 'bg-green-100 text-green-800',
            suspended: 'bg-red-100 text-red-800',
            inactive: 'bg-gray-100 text-gray-600'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    const getTransactionTypeBadge = (type) => {
        if (type === 'purchase') return 'bg-green-100 text-green-800';
        if (type === 'usage') return 'bg-blue-100 text-blue-800';
        return 'bg-gray-100 text-gray-800';
    };

    const filteredTenants = tenants.filter(tenant =>
        tenant.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tenant.subdomain?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) return <LoadingSpinner size="lg" />;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                <div className="shrink-0">
                    <h1 className="text-2xl font-bold text-gray-900">Super Admin - Tenants</h1>
                    <p className="text-gray-600">Create and manage all tenant organizations in the system</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        onClick={fetchTenants}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm transition-colors"
                        title="Refresh"
                    >
                        <RefreshCw className="h-4 w-4 sm:mr-1" />
                        <span className="hidden sm:inline">Refresh</span>
                        <span className="sm:hidden">Sync</span>
                    </button>
                    <button
                        onClick={() => { setShowCreateModal(true); setCreateResult(null); }}
                        className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm transition-colors"
                    >
                        <Building2 className="h-4 w-4 sm:mr-1.5" />
                        <span className="hidden sm:inline">New Tenant</span>
                        <span className="sm:hidden">Add</span>
                    </button>
                </div>
            </div>

            {/* Search */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                    type="text"
                    placeholder="Search tenants by name or subdomain..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg shadow p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Total Tenants</p>
                            <p className="text-2xl font-bold text-gray-900">{tenants.length}</p>
                        </div>
                        <Building2 className="h-8 w-8 text-indigo-600" />
                    </div>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Active</p>
                            <p className="text-2xl font-bold text-green-600">
                                {tenants.filter(t => t.status === 'active').length}
                            </p>
                        </div>
                        <CheckCircle className="h-8 w-8 text-green-600" />
                    </div>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Trial</p>
                            <p className="text-2xl font-bold text-yellow-600">
                                {tenants.filter(t => t.status === 'trial').length}
                            </p>
                        </div>
                        <Clock className="h-8 w-8 text-yellow-600" />
                    </div>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Suspended</p>
                            <p className="text-2xl font-bold text-red-600">
                                {tenants.filter(t => t.status === 'suspended').length}
                            </p>
                        </div>
                        <XCircle className="h-8 w-8 text-red-600" />
                    </div>
                </div>
            </div>

            {/* Tenants Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subdomain</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Users</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assets</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trial Ends</th>
                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredTenants.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                                        <Building2 className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                                        <p>No tenants found</p>
                                    </td>
                                </tr>
                            ) : (
                                filteredTenants.map((tenant) => (
                                    <tr key={tenant.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-4">
                                            <div className="flex items-center">
                                                <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                                                    <Building2 className="h-5 w-5 text-indigo-600" />
                                                </div>
                                                <div className="ml-3">
                                                    <p className="text-sm font-medium text-gray-900">{tenant.company_name}</p>
                                                    <p className="text-xs text-gray-500">ID: {tenant.id}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 text-sm text-gray-600">
                                            {tenant.subdomain}
                                        </td>
                                        <td className="px-4 py-4">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(tenant.status)}`}>
                                                {getStatusIcon(tenant.status)}
                                                <span className="ml-1 capitalize">{tenant.status}</span>
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 text-sm text-gray-600">
                                            {tenant.user_count || 0}
                                        </td>
                                        <td className="px-4 py-4 text-sm text-gray-600">
                                            {tenant.asset_count || 0}
                                        </td>
                                        <td className="px-4 py-4 text-sm text-gray-500">
                                            {tenant.created_at ? new Date(tenant.created_at).toLocaleDateString() : '-'}
                                        </td>
                                        <td className="px-4 py-4 text-sm text-gray-500">
                                            {tenant.trial_ends_at ? new Date(tenant.trial_ends_at).toLocaleDateString() : '-'}
                                        </td>
                                        <td className="px-4 py-4">
                                            <div className="flex items-center justify-center space-x-1">
                                                {/* Edit */}
                                                <button
                                                    onClick={() => openEditModal(tenant)}
                                                    className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                                    title="Edit Tenant"
                                                >
                                                    <Edit3 className="h-4 w-4" />
                                                </button>
                                                
                                                {/* Users */}
                                                <button
                                                    onClick={() => openUsersModal(tenant)}
                                                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                    title="Manage Users"
                                                >
                                                    <UsersIcon className="h-4 w-4" />
                                                </button>

                                                {/* Credits */}
                                                <button
                                                    onClick={() => openCreditsModal(tenant)}
                                                    className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                                    title="Credits & Transactions"
                                                >
                                                    <CreditCard className="h-4 w-4" />
                                                </button>

                                                {/* Suspend / Activate */}
                                                {tenant.status !== 'suspended' ? (
                                                    <button
                                                        onClick={() => { setSuspendTenantId(tenant.id); setSuspendReason(''); setShowSuspendModal(true); }}
                                                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                        title="Suspend Tenant"
                                                    >
                                                        <Ban className="h-4 w-4" />
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() => handleActivateTenant(tenant.id)}
                                                        className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                                        title="Activate Tenant"
                                                    >
                                                        <Play className="h-4 w-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination for Tenants Table */}
            {totalPages > 1 && (
                <Pagination
                    page={page}
                    totalPages={totalPages}
                    total={totalTenants}
                    limit={PAGE_SIZE}
                    onPageChange={(newPage) => setPage(newPage)}
                />
            )}

            {/* ==================== CREATE TENANT MODAL ==================== */}

            <Modal isOpen={showCreateModal} onClose={() => { setShowCreateModal(false); setCreateResult(null); }} title="Create New Tenant" size="lg">
                <form onSubmit={handleCreateTenant} className="space-y-6">
                    {createResult && (
                        <div className={`p-4 rounded-lg ${createResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                            <div className="flex items-start">
                                {createResult.success ? (
                                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                                ) : (
                                    <XCircle className="h-5 w-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                                )}
                                <div>
                                    <p className={`font-medium ${createResult.success ? 'text-green-800' : 'text-red-800'}`}>
                                        {createResult.success ? 'Tenant Created Successfully' : 'Error'}
                                    </p>
                                    <p className={`text-sm mt-1 ${createResult.success ? 'text-green-600' : 'text-red-600'}`}>
                                        {createResult.success 
                                            ? `Tenant ID: ${createResult.data?.tenant?.id || ''} | Landlord: ${createResult.data?.landlord?.email || ''}`
                                            : createResult.error}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Organization Details</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
                                <input
                                    type="text"
                                    name="company_name"
                                    value={formData.company_name}
                                    onChange={handleInputChange}
                                    className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                                    placeholder="e.g. Acme Properties Ltd"
                                    required
                                />
                            </div>
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Subdomain *</label>
                                <div className="flex items-center">
                                    <input
                                        type="text"
                                        name="subdomain"
                                        value={formData.subdomain}
                                        onChange={(e) => setFormData(prev => ({ ...prev, subdomain: e.target.value }))}
                                        className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                                        placeholder="acme-properties"
                                        required
                                    />
                                    <span className="ml-2 text-sm text-gray-500">.brominon.com</span>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">Auto-generated from company name, but can be customized</p>
                            </div>
                        </div>
                    </div>

                    <hr className="border-gray-200" />

                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Landlord Account</h3>
                        <p className="text-sm text-gray-500 mb-3">A landlord user will be created for this tenant organization</p>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                                <input
                                    type="text"
                                    name="landlord_first_name"
                                    value={formData.landlord_first_name}
                                    onChange={handleInputChange}
                                    className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                                    placeholder="John"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                                <input
                                    type="text"
                                    name="landlord_last_name"
                                    value={formData.landlord_last_name}
                                    onChange={handleInputChange}
                                    className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                                    placeholder="Doe"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                                <input
                                    type="email"
                                    name="landlord_email"
                                    value={formData.landlord_email}
                                    onChange={handleInputChange}
                                    className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                                    placeholder="john@acmeproperties.com"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                                <input
                                    type="password"
                                    name="landlord_password"
                                    value={formData.landlord_password}
                                    onChange={handleInputChange}
                                    className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                                    placeholder="Min 8 characters"
                                    required
                                    minLength={8}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                                <input
                                    type="tel"
                                    name="landlord_phone"
                                    value={formData.landlord_phone}
                                    onChange={handleInputChange}
                                    className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                                    placeholder="254700000000"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end space-x-3 pt-4 border-t">
                        <button
                            type="button"
                            onClick={() => { setShowCreateModal(false); setCreateResult(null); }}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={creating}
                            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {creating ? (
                                <span className="flex items-center">
                                    <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    Creating...
                                </span>
                            ) : 'Create Tenant'}
                        </button>
                    </div>
                </form>
            </Modal>

            {/* ==================== EDIT TENANT MODAL ==================== */}
            <Modal isOpen={showEditModal} onClose={() => { setShowEditModal(false); }} title={`Edit Tenant: ${editingTenant?.company_name || ''}`} size="md">
                <form onSubmit={handleEditTenant} className="space-y-6">
                    {editResult && (
                        <div className={`p-4 rounded-lg ${editResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                            <div className="flex items-start">
                                {editResult.success ? (
                                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                                ) : (
                                    <XCircle className="h-5 w-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                                )}
                                <p className={`text-sm ${editResult.success ? 'text-green-600' : 'text-red-600'}`}>
                                    {editResult.success ? editResult.message : editResult.error}
                                </p>
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                            <input
                                type="text"
                                value={editFormData.company_name}
                                onChange={(e) => setEditFormData(prev => ({ ...prev, company_name: e.target.value }))}
                                className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                                required
                            />
                        </div>
                        <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Subdomain</label>
                            <div className="flex items-center">
                                <input
                                    type="text"
                                    value={editFormData.subdomain}
                                    onChange={(e) => setEditFormData(prev => ({ ...prev, subdomain: e.target.value }))}
                                    className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                                    required
                                />
                                <span className="ml-2 text-sm text-gray-500">.brominon.com</span>
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Trial Ends Date</label>
                            <input
                                type="date"
                                value={editFormData.trial_ends_at}
                                onChange={(e) => setEditFormData(prev => ({ ...prev, trial_ends_at: e.target.value }))}
                                className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Current Status</label>
                            <div className="p-2">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(editingTenant?.status)}`}>
                                    {getStatusIcon(editingTenant?.status)}
                                    <span className="ml-1 capitalize">{editingTenant?.status}</span>
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end space-x-3 pt-4 border-t">
                        <button
                            type="button"
                            onClick={() => setShowEditModal(false)}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={editing}
                            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {editing ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </Modal>

            {/* ==================== SUSPEND TENANT MODAL ==================== */}
            <Modal isOpen={showSuspendModal} onClose={() => setShowSuspendModal(false)} title="Suspend Tenant" size="sm">
                <div className="space-y-4">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-start">
                            <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                            <p className="text-sm text-red-700">
                                Suspending this tenant will prevent all users from accessing their account until reactivated.
                            </p>
                        </div>
                    </div>
                    <div className="flex justify-end space-x-3 pt-4 border-t">
                        <button
                            type="button"
                            onClick={() => setShowSuspendModal(false)}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSuspendTenant}
                            disabled={suspending}
                            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {suspending ? 'Suspending...' : 'Suspend Tenant'}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* ==================== USERS MODAL ==================== */}
            <Modal isOpen={showUsersModal} onClose={() => setShowUsersModal(false)} title={`Users: ${usersTenant?.company_name || ''}`} size="lg">
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <p className="text-sm text-gray-600">
                            Total Users: <span className="font-semibold">{users.length}</span>
                        </p>
                        <button
                            onClick={openAddUserModal}
                            className="flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                        >
                            <UserPlus className="h-4 w-4 mr-1" />
                            Add User
                        </button>
                    </div>

                    {usersLoading ? (
                        <div className="flex justify-center py-8">
                            <LoadingSpinner size="md" />
                        </div>
                    ) : users.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            <UsersIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                            <p>No users found for this tenant</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {users.map((user) => (
                                        <tr key={user.id} className="hover:bg-gray-50">
                                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                                {user.first_name} {user.last_name}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-600">{user.email}</td>
                                            <td className="px-4 py-3 text-sm text-gray-600">{user.phone || '-'}</td>
                                            <td className="px-4 py-3">
                                                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${getUserRoleBadge(user.role)}`}>
                                                    {user.role}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${getUserStatusBadge(user.status)}`}>
                                                    {user.status}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center justify-center space-x-1">
                                                    <button
                                                        onClick={() => openEditUserModal(user)}
                                                        className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                                        title="Edit User"
                                                    >
                                                        <Edit3 className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => openChangePasswordModal(user.id, user.email)}
                                                        className="p-1.5 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                                                        title="Change Password"
                                                    >
                                                        <Key className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => confirmDeleteUser(user.id, user.email)}
                                                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                        title="Delete User"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </Modal>

            {/* Add User Modal */}
            <Modal isOpen={showAddUserModal} onClose={() => setShowAddUserModal(false)} title="Add User to Tenant" size="md">
                <form onSubmit={handleAddUser} className="space-y-4">
                    {addUserResult && (
                        <div className={`p-3 rounded-lg ${addUserResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                            <div className="flex items-start">
                                {addUserResult.success ? (
                                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                                ) : (
                                    <XCircle className="h-4 w-4 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                                )}
                                <p className={`text-sm ${addUserResult.success ? 'text-green-600' : 'text-red-600'}`}>
                                    {addUserResult.success ? addUserResult.message : addUserResult.error}
                                </p>
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                            <input
                                type="text"
                                value={addUserForm.first_name}
                                onChange={(e) => setAddUserForm(prev => ({ ...prev, first_name: e.target.value }))}
                                className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 p-2"
                                placeholder="John"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                            <input
                                type="text"
                                value={addUserForm.last_name}
                                onChange={(e) => setAddUserForm(prev => ({ ...prev, last_name: e.target.value }))}
                                className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 p-2"
                                placeholder="Doe"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                            <input
                                type="email"
                                value={addUserForm.email}
                                onChange={(e) => setAddUserForm(prev => ({ ...prev, email: e.target.value }))}
                                className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 p-2"
                                placeholder="user@example.com"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                            <input
                                type="tel"
                                value={addUserForm.phone}
                                onChange={(e) => setAddUserForm(prev => ({ ...prev, phone: e.target.value }))}
                                className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 p-2"
                                placeholder="254700000000"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                            <input
                                type="password"
                                value={addUserForm.password}
                                onChange={(e) => setAddUserForm(prev => ({ ...prev, password: e.target.value }))}
                                className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 p-2"
                                placeholder="Min 6 characters"
                                required
                                minLength={6}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                            <select
                                value={addUserForm.role}
                                onChange={(e) => setAddUserForm(prev => ({ ...prev, role: e.target.value }))}
                                className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 p-2"
                                required
                            >
                                <option value="tenant">Tenant</option>
                                <option value="caretaker">Caretaker</option>
                                <option value="landlord">Landlord</option>
                            </select>
                        </div>
                    </div>

                    <div className="flex justify-end space-x-3 pt-4 border-t">
                        <button
                            type="button"
                            onClick={() => setShowAddUserModal(false)}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={addingUser}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {addingUser ? 'Adding...' : 'Add User'}
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Change Password Modal */}
            <Modal isOpen={showChangePasswordModal} onClose={() => setShowChangePasswordModal(false)} title="Change User Password" size="sm">
                <form onSubmit={handleChangePassword} className="space-y-4">
                    <p className="text-sm text-gray-600">
                        Changing password for: <span className="font-semibold">{passwordUserEmail}</span>
                    </p>

                    {passwordResult && (
                        <div className={`p-3 rounded-lg ${passwordResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                            <div className="flex items-start">
                                {passwordResult.success ? (
                                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                                ) : (
                                    <XCircle className="h-4 w-4 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                                )}
                                <p className={`text-sm ${passwordResult.success ? 'text-green-600' : 'text-red-600'}`}>
                                    {passwordResult.success ? passwordResult.message : passwordResult.error}
                                </p>
                            </div>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">New Password *</label>
                        <input
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 p-2"
                            placeholder="Min 6 characters"
                            required
                            minLength={6}
                        />
                    </div>

                    <div className="flex justify-end space-x-3 pt-4 border-t">
                        <button
                            type="button"
                            onClick={() => setShowChangePasswordModal(false)}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={changingPassword}
                            className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {changingPassword ? 'Changing...' : 'Change Password'}
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Delete User Confirm */}
            <Modal isOpen={showDeleteUserConfirm} onClose={() => setShowDeleteUserConfirm(false)} title="Confirm Delete User" size="sm">
                <div className="space-y-4">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-start">
                            <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                            <div>
                                <p className="text-sm font-medium text-red-800">Are you sure?</p>
                                <p className="text-sm text-red-600 mt-1">
                                    You are about to delete user <strong>{deleteUserEmail}</strong>. This action cannot be undone.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={() => setShowDeleteUserConfirm(false)}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleDeleteUser}
                            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                        >
                            Delete User
                        </button>
                    </div>
                </div>
            </Modal>

            {/* ==================== EDIT USER MODAL ==================== */}
            <Modal isOpen={showEditUserModal} onClose={() => { setShowEditUserModal(false); setEditUserResult(null); }} title="Edit User" size="md">
                <form onSubmit={handleEditUser} className="space-y-4">
                    {editUserResult && (
                        <div className={`p-3 rounded-lg ${editUserResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                            <div className="flex items-start">
                                {editUserResult.success ? (
                                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                                ) : (
                                    <XCircle className="h-4 w-4 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                                )}
                                <p className={`text-sm ${editUserResult.success ? 'text-green-600' : 'text-red-600'}`}>
                                    {editUserResult.success ? editUserResult.message : editUserResult.error}
                                </p>
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                            <input
                                type="text"
                                value={editUserForm.first_name}
                                onChange={(e) => setEditUserForm(prev => ({ ...prev, first_name: e.target.value }))}
                                className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                                placeholder="John"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                            <input
                                type="text"
                                value={editUserForm.last_name}
                                onChange={(e) => setEditUserForm(prev => ({ ...prev, last_name: e.target.value }))}
                                className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                                placeholder="Doe"
                                required
                            />
                        </div>
                        <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                            <input
                                type="email"
                                value={editUserForm.email}
                                onChange={(e) => setEditUserForm(prev => ({ ...prev, email: e.target.value }))}
                                className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                                placeholder="user@example.com"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                            <input
                                type="tel"
                                value={editUserForm.phone}
                                onChange={(e) => setEditUserForm(prev => ({ ...prev, phone: e.target.value }))}
                                className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                                placeholder="254700000000"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                            <select
                                value={editUserForm.role}
                                onChange={(e) => setEditUserForm(prev => ({ ...prev, role: e.target.value }))}
                                className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                                required
                            >
                                <option value="tenant">Tenant</option>
                                <option value="caretaker">Caretaker</option>
                                <option value="landlord">Landlord</option>
                                <option value="superadmin">Super Admin</option>
                            </select>
                        </div>
                    </div>

                    <div className="flex justify-end space-x-3 pt-4 border-t">
                        <button
                            type="button"
                            onClick={() => { setShowEditUserModal(false); setEditUserResult(null); }}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={editingUserSaving}
                            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {editingUserSaving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </Modal>

            {/* ==================== CREDITS MODAL ==================== */}
            <Modal isOpen={showCreditsModal} onClose={() => setShowCreditsModal(false)} title={`Credits: ${creditsTenant?.company_name || ''}`} size="lg">
                <div className="space-y-6">
                    {creditsLoading ? (
                        <div className="flex justify-center py-8">
                            <LoadingSpinner size="md" />
                        </div>
                    ) : (
                        <>
                            {/* Credits Balance Card */}
                            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-green-700 font-medium">Current Credits Balance</p>
                                        <p className="text-3xl font-bold text-green-700 mt-1">
                                            {creditsData.balance.toLocaleString()}
                                        </p>
                                        <p className="text-xs text-green-600 mt-1">
                                            Cost per SMS: KES 1.50 | 1 credit per SMS
                                        </p>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <DollarSign className="h-10 w-10 text-green-500" />
                                    </div>
                                </div>
                            </div>

                            {/* Add Credits Button */}
                            <div className="flex justify-end">
                                <button
                                    onClick={openAddCreditsModal}
                                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                                >
                                    <DollarSign className="h-4 w-4 mr-1" />
                                    Add Credits Manually
                                </button>
                            </div>

                            {/* Transactions Table */}
                            <div>
                                <h4 className="text-sm font-semibold text-gray-700 mb-2">Transaction History</h4>
                                {creditsData.transactions.length === 0 ? (
                                    <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg">
                                        <CreditCard className="h-8 w-8 mx-auto mb-1 text-gray-300" />
                                        <p className="text-sm">No transactions found</p>
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto border border-gray-200 rounded-lg">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount (KES)</th>
                                                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Credits</th>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Payment Method</th>
                                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {creditsData.transactions.map((txn, idx) => (
                                                    <tr key={txn.id || idx} className="hover:bg-gray-50">
                                                        <td className="px-3 py-2 text-sm text-gray-600 whitespace-nowrap">
                                                            {txn.created_at ? new Date(txn.created_at).toLocaleDateString() : '-'}
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${getTransactionTypeBadge(txn.type)}`}>
                                                                {txn.type}
                                                            </span>
                                                        </td>
                                                        <td className="px-3 py-2 text-sm text-right text-gray-900 font-medium">
                                                            {txn.amount ? `KES ${parseFloat(txn.amount).toLocaleString()}` : '-'}
                                                        </td>
                                                        <td className="px-3 py-2 text-sm text-right text-gray-900 font-medium">
                                                            {txn.credits || '-'}
                                                        </td>
                                                        <td className="px-3 py-2 text-sm text-gray-600">
                                                            {txn.payment_method || '-'}
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${txn.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                                                {txn.status || 'completed'}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </Modal>

            {/* Add Credits Modal */}
            <Modal isOpen={showAddCreditsModal} onClose={() => setShowAddCreditsModal(false)} title="Add Credits Manually" size="sm">
                <form onSubmit={handleAddCredits} className="space-y-4">
                    <p className="text-sm text-gray-600">
                        Adding credits to: <span className="font-semibold">{creditsTenant?.company_name}</span>
                    </p>

                    {addCreditsResult && (
                        <div className={`p-3 rounded-lg ${addCreditsResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                            <div className="flex items-start">
                                {addCreditsResult.success ? (
                                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                                ) : (
                                    <XCircle className="h-4 w-4 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                                )}
                                <p className={`text-sm ${addCreditsResult.success ? 'text-green-600' : 'text-red-600'}`}>
                                    {addCreditsResult.success ? addCreditsResult.message : addCreditsResult.error}
                                </p>
                            </div>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Number of Credits *</label>
                        <input
                            type="number"
                            value={addCreditsForm.credits}
                            onChange={(e) => setAddCreditsForm(prev => ({ ...prev, credits: e.target.value }))}
                            className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 p-2"
                            placeholder="e.g. 100"
                            required
                            min="1"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Amount (KES) <span className="text-gray-400 font-normal">(optional - defaults to credits × 1.5)</span>
                        </label>
                        <input
                            type="number"
                            value={addCreditsForm.amount}
                            onChange={(e) => setAddCreditsForm(prev => ({ ...prev, amount: e.target.value }))}
                            className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 p-2"
                            placeholder="e.g. 150"
                            min="0"
                            step="0.01"
                        />
                    </div>

                    <div className="flex justify-end space-x-3 pt-4 border-t">
                        <button
                            type="button"
                            onClick={() => setShowAddCreditsModal(false)}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={addingCredits}
                            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {addingCredits ? 'Adding...' : 'Add Credits'}
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default SuperAdminTenants;
